import { useState, useCallback } from "react"
import type { TamCalculatorFilters } from "~/react/types/tam_calculator"

interface UseTamCalculatorFiltersReturn {
  filters: TamCalculatorFilters
  setFilters: React.Dispatch<React.SetStateAction<TamCalculatorFilters>>
  updateFilter: <K extends keyof TamCalculatorFilters>(
    key: K,
    value: TamCalculatorFilters[K]
  ) => void
  clearFilter: (key: keyof TamCalculatorFilters) => void
  clearAllFilters: () => void
  hasFilters: boolean
}

export function useTamCalculatorFilters(
  initialFilters: TamCalculatorFilters = {}
): UseTamCalculatorFiltersReturn {
  const [filters, setFilters] = useState<TamCalculatorFilters>(initialFilters)

  const updateFilter = useCallback(
    <K extends keyof TamCalculatorFilters>(key: K, value: TamCalculatorFilters[K]) => {
      setFilters((prev) => {
        const newFilters = { ...prev }
        // Remove key if value is empty (null, undefined, empty array, empty string, 0)
        const isEmpty =
          value === null ||
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && value.length === 0) ||
          (key === "acv" && (value === 0 || value === "0"))
        if (isEmpty) {
          delete newFilters[key]
        } else {
          newFilters[key] = value
        }
        return newFilters
      })
    },
    []
  )

  const clearFilter = useCallback((key: keyof TamCalculatorFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev }
      delete newFilters[key]
      return newFilters
    })
  }, [])

  const clearAllFilters = useCallback(() => {
    setFilters({})
  }, [])

  // Check if there are any meaningful filter values (excluding ACV for "has filters" check)
  const hasFilters = Object.entries(filters).some(([key, value]) => {
    // ACV alone doesn't count as "having filters" since we need company filters too
    if (key === "acv") return false
    if (value === null || value === undefined || value === "") return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === "object") return Object.keys(value).length > 0
    return true
  })

  return {
    filters,
    setFilters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
  }
}

